'use client'

import { useHeaderTheme } from '@/providers/HeaderTheme'
import React, { useEffect, useRef } from 'react'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'

import type { Page } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import InfoTag from '@/components/ui/InfoTag'

export const SingleHero: React.FC<Page['hero']> = ({ singleHero, settings: _settings }) => {
  const { setHeaderTheme } = useHeaderTheme()
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60])

  useEffect(() => {
    setHeaderTheme('dark')
  })

  if (!singleHero) return null

  const { infotag, title, subtitle, media, links } = singleHero
  const hasInfotag = Boolean(infotag)

  const getHeightClass = () => {
    // Default to full screen height since height setting is not available in heroFields
    return 'h-dvh'
  }

  return (
    <section
      ref={sectionRef}
      className={`relative ${getHeightClass()} min-h-dvh overflow-hidden`}
      data-theme="dark"
    >
      {/* Background image with parallax */}
      <motion.div className="absolute inset-0 will-change-transform" style={{ y }}>
        {media && typeof media === 'object' && (
          <Media
            fill
            pictureClassName="absolute inset-0 w-full h-full"
            imgClassName="object-cover"
            priority
            fetchPriority="high"
            resource={media}
            size="100vw"
          />
        )}
      </motion.div>

      {/* Gradient overlay for better text contrast */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/35 to-transparent" />

      {/* Content */}
      <div className="absolute inset-x-0 bottom-12">
        <div className="container mx-auto px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key="single-hero-content"
              className="flex flex-col gap-3 items-start justify-start max-w-[1000px]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {hasInfotag && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.15,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <InfoTag as="h1" size="md">
                    {infotag}
                  </InfoTag>
                </motion.div>
              )}
              {hasInfotag ? (
                <motion.span
                  className="display-lg on-dark drop-shadow-[0_16px_32px_rgba(12,12,13,0.4)] uppercase"
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {title}
                </motion.span>
              ) : (
                <motion.h1
                  className="display-lg on-dark drop-shadow-[0_16px_32px_rgba(12,12,13,0.4)] uppercase"
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {title}
                </motion.h1>
              )}
              {subtitle && (
                <motion.p
                  className="subtitle-lg on-dark drop-shadow-[0_8px_16px_rgba(12,12,13,0.3)]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.3,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {subtitle}
                </motion.p>
              )}
              {Array.isArray(links) && links.length > 0 && (
                <motion.div
                  className="mt-4 flex flex-wrap gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.35,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {links.map(({ link }, i) => {
                    if (!link) return null

                    return (
                      <CMSLink
                        key={i}
                        {...link}
                        newTab={link.openInNewTab}
                        appearance="external"
                        size="lg"
                        externalColor="light"
                        className="subtitle-lg"
                      />
                    )
                  })}
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
